import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import cn from 'classnames';

import {
  Button,
  CircleButton,
  EasyModal,
  Modal,
  PageHeader,
  PermissionAccessWrapper,
  Skeleton,
} from '@ui';
import { TrashIcon } from '@icons';
import RecordMeta from '@app/components/audio-build/record-meta';
import AudioChat from '@app/components/audio-build/audio-chat';
import { useLocation, useNavigate } from 'react-router-dom';
import useTranslation from '@app/hooks/use-translation';
import connector, { PropsFromRedux } from '@app/utils/store';
import { useAudioContext } from '@app/components/audio-build/record-provider';
import WaveAudio from '@app/components/audio-build/waveaudio/wave-audio.component';
import { CalculatedRecordMetric, Dialog } from '@app/interfaces/records';

import { userPermissions } from '@app/utils/user-permissions';

import {
  useDeleteChatMutation,
  useGetChatDetailsQuery,
} from '@app/store/api/chat-api/all-chats.api';

import { MetaRecords } from '@app/store/reducers/meta.slice';

import { useGetAppealByIdQuery } from '@app/store/api/qm-module/qm-appeal/qm-appeal.api';

import { useLazyRecalculateGptGroupManualQuery } from '@app/store/api/gpt-metric.api';

import { GPTGroupMetricListObjectType } from '@app/interfaces/gpt-metric.type';

import RecordMetric from './record-metric';
import { RecordMonitoring } from './record-monitoring';

const AudioContainer: FC<PropsFromRedux> = (props) => {
  const {
    removeRecordRequest,
    recordsState,
    getFileRecordThunk,
    compositeRecordRequest,
    recordMetaRequest,
    metaState,
  } = props;
  const {
    recordId,
    projectId,
    currentTemplate,
    recordMfId,
    recordMonitoring,
    appeal_id,
    gptGroupsList,
  } = useAudioContext();
  const mainContainerRef = useRef<HTMLDivElement>(null!);
  const { compositeRecord, audio, isPending, recordIsPending } = recordsState;
  //interruptions, pause-exceed, max-pause-duration,max-monolog-duration
  const { isPending: isPendingMeta, recordMeta } = metaState;
  const [currentMetricByRegionId, setCurrentMetricByRegionId] = useState<number>(0);
  const [activeMetric, changeActiveMetric] = useState<CalculatedRecordMetric[string]>();
  const [isFullScreen, setFullScreen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [currentAsrModel, changeAsrModel] = useState<string>();
  const [seeFullTextModal, setFullTextModal] = useState<{ text: string; name: string }>();
  // calculatedRecordMetricVoice
  const [calculatedRecordMetricVoice, changeCalculatedRecordMetricVoice] = useState(
    compositeRecord?.calculatedRecordMetric,
  );

  const [gptGroupIdForRecalculate, changeGptGroupIdForRecalculate] = useState<string>();

  const [recalculateGroup] = useLazyRecalculateGptGroupManualQuery();

  function handleSetGroupToRecalculate(group_id: string) {
    changeGptGroupIdForRecalculate(group_id);
  }
  function onCloseManualRecalcModal() {
    changeGptGroupIdForRecalculate(undefined);
  }
  function handleApproveRecalculateGptGroupManual() {
    try {
      handleRecalculateGptGroupResult();
    } finally {
      changeGptGroupIdForRecalculate(undefined);
    }
  }
  function handleRecalculateGptGroupResult() {
    if (!gptGroupIdForRecalculate) return;
    recalculateGroup({ gpt_group_id: gptGroupIdForRecalculate, record_id: recordId })
      .unwrap()
      .then((result) => {
        const copy = { ...calculatedRecordMetricVoice };
        for (const res of result.result.data) {
          const metricId = res?.metric_id;
          copy[metricId].value = res?.value;

          if (copy[metricId].settings?.gpt_response) {
            (copy[metricId].settings as any).gpt_response.code = res.cases.code;
          }
        }
        changeCalculatedRecordMetricVoice(copy);
      });
  }

  useEffect(() => {
    changeCalculatedRecordMetricVoice(compositeRecord?.calculatedRecordMetric);
  }, [compositeRecord?.calculatedRecordMetric]);
  // calculatedRecordMetricVoice

  const [deleteChat] = useDeleteChatMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const pageFrom = location.pathname.split('/')[1];
  const { t } = useTranslation('components.audio');
  const [activeChatTiming, setActiveChatTiming] = useState<
    { from: number; to: number } | undefined
  >();
  const { data: appealObject, isLoading: appealLoading } = useGetAppealByIdQuery(
    { appellation_id: appeal_id || '' },
    { skip: !appeal_id },
  );
  const { data: chatRecord, isLoading: chatLoading } = useGetChatDetailsQuery(
    {
      chat_id: recordId,
      project_id: projectId,
    },
    { skip: currentTemplate === 'voice' },
  );

  const chatMeta = useMemo(() => {
    const result: Partial<MetaRecords> = {};
    for (const key in chatRecord?.chat?.metadata) {
      result[key] = chatRecord?.chat?.metadata[key].reduce((acc, current) => {
        acc[current.alias || current.meta_name] = current.meta_value;
        return acc;
      }, {});
    }
    return result;
  }, [chatRecord?.chat?.metadata]) as MetaRecords;
  const chatDialog: Dialog[] = useMemo(() => {
    return chatRecord?.chatDialog || [];
  }, [chatRecord]);

  const metaOfRecord = {
    chat: chatMeta,
    voice: recordMeta,
  };

  const dialogs = {
    chat: chatDialog,
    voice: compositeRecord?.dialogs,
  };

  const calculatedRecordMetric = {
    chat: chatRecord?.calculatedRecordMetric,
    voice: calculatedRecordMetricVoice,
  };

  const known_metrics_cases = {
    chat: chatRecord?.known_metrics_cases_chat,
    voice: compositeRecord?.known_metrics_cases,
  };

  const name = {
    chat: `${chatRecord?.chat?.METAChatID} ${chatRecord?.chat?.METAdatetime}`,
    voice: recordMeta?.record_name,
  };

  const [activeMessageId, setActiveMessageId] = useState<Array<string> | null>(null);
  useEffect(() => {
    if (currentTemplate === 'chat') return;
    recordMetaRequest({ record_id: recordId });
  }, [currentTemplate, recordId, recordMetaRequest]);
  const was = useRef(false);
  useEffect(() => {
    if (currentTemplate === 'chat') return;
    if (was.current) return;
    compositeRecordRequest({ currentAsrModel, projectId, recordId });
    was.current = true;
  }, [compositeRecordRequest, currentAsrModel, currentTemplate, projectId, recordId]);

  useEffect(() => {
    if (currentTemplate === 'chat') return;
    getFileRecordThunk({ recordId });
  }, [currentTemplate, getFileRecordThunk, projectId, recordId]);

  function removeRecord() {
    if (recordId) {
      if (currentTemplate === 'chat') {
        deleteChat({ items: [recordId] });
      } else {
        removeRecordRequest({ items: [recordId] });
      }
      navigate(window.location.pathname.split('/').splice(0, 2).join('/'));
    }
  }

  function handleSetFullTextToSee(obj: { text: string; name: string }) {
    setFullTextModal(obj);
  }

  function handleUpdateRecord(asrModel: string) {
    changeAsrModel(asrModel);
  }

  async function handleCopyText() {
    // eslint-disable-next-line compat/compat
    await window.navigator.clipboard.writeText(window.location.href);
    toast.success(t('link_copied'));
  }
  function handleChangeActiveRecordMetric(
    metricId: string | undefined,
    type: 'once' | 'all' | 'vad',
  ) {
    switch (type) {
      case 'vad':
        {
          if (metricId) {
            const cur_metric = calculatedRecordMetric[currentTemplate || 'voice']?.[metricId];
            changeActiveMetric(cur_metric);
          } else {
            changeActiveMetric(undefined);
          }
        }
        break;
      case 'once': {
        mainContainerRef.current.scrollIntoView({ behavior: 'smooth' });
        if (metricId) {
          const currentIds =
            calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]?.settings?.ids || [];
          if (calculatedRecordMetric[currentTemplate || 'voice']?.[metricId] !== activeMetric) {
            changeActiveMetric(calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]);
            setCurrentMetricByRegionId(0);
            currentIds &&
              setActiveMessageId([
                String(
                  calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]?.settings
                    ?.messageId?.[0],
                ),
              ]);
          } else {
            let currentRegion = 0;
            if (currentIds.length - 1 > currentMetricByRegionId) {
              setCurrentMetricByRegionId((prevState) => prevState + 1);
              currentRegion = currentMetricByRegionId + 1;
            } else {
              setCurrentMetricByRegionId(0);
            }
            setActiveMessageId([
              calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]?.settings?.messageId?.[
                currentRegion
              ] || '',
            ]);
          }
        }
        break;
      }
      case 'all': {
        if (
          metricId !== undefined &&
          calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]
        ) {
          changeActiveMetric(calculatedRecordMetric[currentTemplate || 'voice']?.[metricId]);
        } else {
          changeActiveMetric(undefined);
        }
        break;
      }
    }
  }

  const renderMetricAndMetaBlocks = (
    <div
      className={cn(
        `${currentTemplate === 'chat' ? 'w-full basis-[48%]' : 'mt-[12px]'}`,
        isFullScreen && 'order-2 basis-1/2 pr-[10px] items-center flex-row w-full',
        recordMfId && 'flex-col-reverse flex',
      )}
    >
      <div className="">
        {isPendingMeta ? (
          <Skeleton width="100%" height={62} />
        ) : (
          <RecordMeta defaultStatus={!projectId} meta={metaOfRecord[currentTemplate || 'voice']} />
        )}
      </div>
      {projectId &&
        calculatedRecordMetric[currentTemplate || 'voice'] &&
        Object.keys(calculatedRecordMetric[currentTemplate || 'voice'] || []).length > 0 && (
          <div className={`${recordMfId ? 'mb-[12px]' : 'my-[12px]'}`}>
            {/* TODO: */}
            <RecordMetric
              handleRecalculateGptGroupResult={handleSetGroupToRecalculate}
              defaultStatus={!recordMfId}
              currentTemplate={currentTemplate}
              metricCases={known_metrics_cases[currentTemplate || 'voice']}
              onChangeActiveMetric={handleChangeActiveRecordMetric}
              projectMetric={
                calculatedRecordMetric[currentTemplate || 'voice'] as CalculatedRecordMetric
              }
              recordIsCalculated={compositeRecord?.recordIsCalculated}
              handleSetFullTextToSee={handleSetFullTextToSee}
            />
          </div>
        )}
      {recordMfId ? (
        recordMonitoring?.recordMonitoringLoading || appealLoading ? (
          <Skeleton width="100%" height={62} />
        ) : (
          <div className="mb-[12px]">
            <RecordMonitoring
              appealObject={appealObject}
              recordMfId={recordMfId}
              recordMonitoringData={recordMonitoring?.recordMonitoringData}
            />
          </div>
        )
      ) : (
        false
      )}
    </div>
  );

  const leftSideMenuMarkupByType = {
    chat: renderMetricAndMetaBlocks,
    voice: (
      <div className={cn(isFullScreen ? 'basis-[100%]  flex-1 w-[100%]' : 'basis-[48%] order-1')}>
        {currentTemplate === 'voice' ? (
          !recordIsPending && audio && compositeRecord?.dialog_words ? (
            <div className={cn(isFullScreen ? 'basis-2/2' : 'basis-1/2')}>
              <WaveAudio
                recordId={recordId}
                wordsTimeList={compositeRecord?.wordsTimeList}
                audio={audio}
                emotionTrack={compositeRecord.emotionTrack}
                activeAndChangeCurrentRegion={[currentMetricByRegionId, setCurrentMetricByRegionId]}
                channelSettings={compositeRecord?.dialogChannels}
                activeMetricWords={activeMetric}
                onChangeActiveMessageId={setActiveMessageId}
                activeMessageId={activeMessageId}
                activeChartTiming={activeChatTiming}
                changeFullScreen={setFullScreen.bind(null, (prev) => !prev)}
                dialogs={compositeRecord?.dialogs}
              />
            </div>
          ) : (
            <Skeleton height={286} width="100%" label={t('skeleton_loading_label')} />
          )
        ) : null}

        {!isFullScreen && renderMetricAndMetaBlocks}
      </div>
    ),
  };
  const headerDescriptor = { chat: t('chat'), voice: t('audio') };

  return (
    <div ref={mainContainerRef} className="">
      <PageHeader
        label={
          <div className="text-speech_analitics">
            <div className="mb-0">{headerDescriptor[currentTemplate]}</div>
            <div className="text-speech_analitics text-[13px]">
              {isPendingMeta ? (
                <Skeleton height="14px" width="100px" />
              ) : (
                <span
                  className="mb-[2px] pl-[0px] cursor-pointer hover:opacity-80"
                  onClick={handleCopyText}
                >
                  {(name && name[currentTemplate || 'voice']) || '-'}
                </span>
              )}
              <CircleButton
                hintTitle={t('popup_hints.copy_link')}
                hintPlace="top"
                icon="CopyIcon"
                size={15}
                className="text-defaultColor ml-[14px]"
                onClick={handleCopyText}
              />
            </div>
          </div>
        }
      >
        {(pageFrom === 'records' || pageFrom === 'chats') && (
          <PermissionAccessWrapper
            permissions={'action'}
            tag={
              currentTemplate === 'chat'
                ? userPermissions.action.actionDeleteChat
                : userPermissions.action.actionDeleteRecord
            }
          >
            <button
              className="text-basic_red p-[5px] inline-flex items-center"
              onClick={setIsDeleteOpen.bind(null, true)}
            >
              <TrashIcon size={15} className="text-basic_red mr-[5px]" />
              {t('delete')}
            </button>
          </PermissionAccessWrapper>
        )}
      </PageHeader>

      <div className={cn('flex w-full', isFullScreen ? ' flex-wrap' : ' gap-[12px]')}>
        {leftSideMenuMarkupByType[currentTemplate || 'voice']}
        <div
          className={cn(
            'basis-1/2 sticky top-[12px]  h-full]',
            isFullScreen ? 'order-3 mt-[12px]' : ' order-2',
          )}
        >
          {isPending || chatLoading ? (
            <Skeleton height="100%" width="100%" />
          ) : (
            <AudioChat
              activeMetric={activeMetric}
              activeMessageId={activeMessageId}
              onChangeActiveChartTiming={setActiveChatTiming}
              transcription={dialogs[currentTemplate || 'voice']}
              currentAsrModel={compositeRecord?.currentAsrModel}
              handleUpdateRecordByAsrModel={handleUpdateRecord}
              audioBoolean={
                currentTemplate === 'chat' ? false : compositeRecord?.recordIsRecognized
              }
              currentTemplate={currentTemplate || 'voice'}
            />
          )}
        </div>
        {isFullScreen && renderMetricAndMetaBlocks}
      </div>
      <Modal
        id="remove_record"
        value={isDeleteOpen}
        size="xs"
        title={t('delete_record')}
        setValue={setIsDeleteOpen}
        onApply={removeRecord}
        variant="removal"
      >
        {t('record_will_deleted')}
      </Modal>
      <EasyModal
        withoutContentPadding
        withoutFooter
        show={!!seeFullTextModal}
        label={seeFullTextModal?.name}
        onClose={() => setFullTextModal(undefined)}
      >
        <div className="flex flex-col gap-[20px] relative">
          <div className=" p-[24px] text-[15px] font-medium whitespace-pre-wrap">
            {seeFullTextModal?.text}
          </div>
          <div className="sticky bottom-0 bg-white py-[12px] flex items-center justify-center">
            <Button onClick={() => setFullTextModal(undefined)} fill="linked" label={t('close')} />
          </div>
        </div>
      </EasyModal>

      {/* пересчет гпт группы в ручную  */}
      <EasyModal
        show={!!gptGroupIdForRecalculate}
        variant="medium484"
        onClose={onCloseManualRecalcModal}
        withoutContentPadding
        withoutFooter
      >
        <div className="relative p-[24px]">
          <div className="text-center text-[16px] font-medium text-1color pb-[24px]">
            Будет произведен расчет группы GPT-метрик
            <br />
            <b>
              {'"'}
              {(gptGroupsList as GPTGroupMetricListObjectType)?.gpt_groups?.find(
                (group) => group.gpt_group_id === gptGroupIdForRecalculate,
              )?.name || 'N/A'}
              {'"'}
            </b>
          </div>
          <div className="sticky bottom-0 bg-white">
            <Button fill="outlined" onClick={handleApproveRecalculateGptGroupManual} label="Да" />
            <Button fill="linked" onClick={onCloseManualRecalcModal} label="Отмена" />
          </div>
        </div>
      </EasyModal>
      {/* пересчет гпт группы в ручную  */}
    </div>
  );
};

export default connector(AudioContainer);
